import { useMemo, useSyncExternalStore } from 'react';
import { getRemotionEnvironment, Html5Audio, Html5Video, OffthreadVideo } from 'remotion';
import { Video as MediaBunnyRenderer, VideoObjectFit } from '@remotion/media';
import { VideoElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import { useRemotionEnvVariables } from '../../../MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionEnvVariables';
import HlsVideo from '../../HlsVideo';
import { useSegmentTransitionVolume } from '@/App/remotion/components/SegmentTransitionWrapper/SegmentTransitionVolumeContext';
import { usePlayRangeCutFade } from '@/App/remotion/components/SegmentComponent/PlayRangeCutFadeContext';
import { useRemotionUsage } from '@/App/remotion/MainPlayer/RemotionUsageContext/RemotionUsageContext';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';

enum VideoRendererType {
  HTML5_AUDIO = 'HTML5_AUDIO',
  MEDIA_BUNNY = 'MEDIA_BUNNY',
  OFFTHREAD = 'OFFTHREAD',
  HLS = 'HLS',
  HTML5_VIDEO = 'HTML5_VIDEO'
}

export const VideoRenderer = ({
  element,
  ignoreEndAt,
  disableMediaFragmentHints,
  handleVideoLoad
}: {
  element: VideoElement;
  ignoreEndAt?: boolean;
  disableMediaFragmentHints?: boolean;
  handleVideoLoad: () => void;
}) => {
  const { details, fromFrame, toFrame, style } = element;
  const objectFit = (style.objectFit as VideoObjectFit | undefined) ?? 'contain';
  const isPreview = !getRemotionEnvironment().isRendering;
  const { useMediaBunnyVideoRenderer, isGeneratingThumbnail } = useRemotionEnvVariables();

  const { useNewVideoTag } = useRemotionUsage();

  const videoSrc: string = useMemo(() => {
    if (isPreview && details.preview) {
      return details.preview;
    }
    if (isGeneratingThumbnail && details.preview) {
      return details.preview;
    }

    return details.src;
  }, [details.src, isPreview, details.preview, isGeneratingThumbnail]);

  const isAudio = useMemo(() => {
    return new URL(videoSrc).pathname.endsWith('.mp3');
  }, [videoSrc]);

  const videoType = useMemo(() => {
    /**
     * If We use <Audio /> in preview, then as soon as speaker switches Audio is lost, Hence we are using <Video /> tag in preview
     * If We don't use <Audio /> tag during rendering, then the render fails internal server issue,
     * Hence in Render we're using Audio tag but in preview we
     */
    if (!isPreview && isAudio) {
      return VideoRendererType.HTML5_AUDIO;
    }

    if (useNewVideoTag || useMediaBunnyVideoRenderer) {
      return VideoRendererType.MEDIA_BUNNY;
    }

    if (!isPreview) {
      return VideoRendererType.OFFTHREAD;
    }

    if (details.src.includes('hls')) {
      return VideoRendererType.HLS;
    }

    return VideoRendererType.HTML5_VIDEO;
  }, [isPreview, isAudio, useMediaBunnyVideoRenderer, details.src, useNewVideoTag]);

  const endAt = useMemo(() => {
    if (ignoreEndAt) {
      return undefined;
    }

    return toFrame;
  }, [ignoreEndAt, toFrame]);

  const baseVolume = useMemo(() => {
    return element.details.muted ? 0 : Math.round(details.volume[0].value) / 100;
  }, [element.details.muted, details.volume]);

  const segmentTransitionVolumeMultiplier = useSegmentTransitionVolume();
  const playRangeCutFadeMultiplier = usePlayRangeCutFade();
  const volume = baseVolume * segmentTransitionVolumeMultiplier * playRangeCutFadeMultiplier;

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isPlaybackSpeedEnabled = !!featureFlags[FeatureFlagKeys.Use_CL_Playback_Speed];

  const pauseWhenBuffering = !isAudio;

  switch (videoType) {
    case VideoRendererType.HTML5_AUDIO:
      return (
        <Html5Audio
          crossOrigin="anonymous"
          trimBefore={fromFrame}
          trimAfter={endAt}
          src={disableMediaFragmentHints ? `${videoSrc}#disable` : videoSrc}
          volume={volume}
          style={{ pointerEvents: 'none', ...style }}
          onError={e => console.error(e)}
          pauseWhenBuffering={pauseWhenBuffering}
          onCanPlayThrough={handleVideoLoad}
        />
      );
    case VideoRendererType.MEDIA_BUNNY:
      if (isPlaybackSpeedEnabled) {
        return (
          <>
            <MediaBunnyRenderer
              trimBefore={fromFrame}
              trimAfter={endAt}
              src={disableMediaFragmentHints ? `${videoSrc}#disable` : videoSrc}
              volume={0}
              objectFit={objectFit}
              style={{ pointerEvents: 'none', ...style }}
            />
            {/*
             * MediaBunnyRenderer plays audio via AudioBufferSourceNode which has no
             * preservesPitch support — pitch shifts at any speed != 1x. Html5Audio
             * creates an HTMLAudioElement whose playbackRate is managed by Remotion's
             * useMediaPlayback; preservesPitch defaults to true so pitch is preserved.
             */}
            <Html5Audio
              crossOrigin="anonymous"
              trimBefore={fromFrame}
              trimAfter={endAt}
              src={disableMediaFragmentHints ? `${videoSrc}#disable` : videoSrc}
              volume={volume}
              pauseWhenBuffering={pauseWhenBuffering}
              onError={e => console.error(e)}
            />
          </>
        );
      }
      return (
        <MediaBunnyRenderer
          trimBefore={fromFrame}
          trimAfter={endAt}
          src={disableMediaFragmentHints ? `${videoSrc}#disable` : videoSrc}
          volume={volume}
          objectFit={objectFit}
          style={{ pointerEvents: 'none', ...style }}
        />
      );
    case VideoRendererType.OFFTHREAD:
      return (
        <OffthreadVideo
          crossOrigin="anonymous"
          trimBefore={fromFrame}
          trimAfter={endAt}
          src={disableMediaFragmentHints ? `${videoSrc}#disable` : videoSrc}
          volume={volume}
          style={{ pointerEvents: 'none', ...style }}
          onError={e => console.error(e)}
          pauseWhenBuffering={pauseWhenBuffering}
        />
      );
    case VideoRendererType.HLS:
      return (
        <HlsVideo
          crossOrigin="anonymous"
          trimBefore={fromFrame}
          trimAfter={endAt}
          src={disableMediaFragmentHints ? `${videoSrc}#disable` : videoSrc}
          volume={volume}
          style={{ pointerEvents: 'none', ...style }}
          onError={e => console.error(e)}
          pauseWhenBuffering={pauseWhenBuffering}
          onCanPlayThrough={handleVideoLoad}
        />
      );
    case VideoRendererType.HTML5_VIDEO:
      return (
        <Html5Video
          crossOrigin="anonymous"
          trimBefore={fromFrame}
          trimAfter={endAt}
          src={disableMediaFragmentHints ? `${videoSrc}#disable` : videoSrc}
          volume={volume}
          style={{ pointerEvents: 'none', ...style }}
          onError={e => console.error(e)}
          pauseWhenBuffering={pauseWhenBuffering}
          onCanPlayThrough={handleVideoLoad}
        />
      );
  }
};
